import React, { useState, useEffect, useCallback } from 'react';
import SupplierLayout from '@/components/SupplierLayout';
import { TrendingUp, Users, Calendar, Package, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SupplierDashboard({ user }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(8);
  const [region, setRegion] = useState('all');

  const fetchForecast = useCallback(async () => {
    try {
      const params = new URLSearchParams({ weeks_ahead: weeks });
      if (region !== 'all') params.append('region', region);
      
      const res = await axios.get(`${API}/varieties/demand-forecast?${params}`, { 
        withCredentials: true 
      });
      setForecast(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [weeks, region]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  const exportCSV = () => {
    if (!forecast?.demand) return;
    
    const csv = [
      ['Crop', 'Variety', 'Growers', 'Plants Needed', 'Peak Planting Month'],
      ...forecast.demand.map(d => [
        d.crop_name,
        d.variety,
        d.total_growers,
        d.total_plants,
        d.planting_dates[0]?.substring(0, 7) || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `homegrown-demand-forecast-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <SupplierLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </SupplierLayout>
    );
  }

  return (
    <SupplierLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Demand Intelligence
          </h1>
          <p className="text-base text-[#5C665C] mt-2">
            Real-time planting intentions from {forecast?.total_growers || 0} home gardeners
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 border border-[#1A4331]/5 flex flex-wrap gap-4 items-center">
          <div>
            <label className="text-sm font-semibold text-[#2D241E] mr-2">Forecast window:</label>
            <select 
              value={weeks} 
              onChange={(e) => setWeeks(parseInt(e.target.value))}
              className="px-4 py-2 bg-[#F0F2EB] rounded-lg"
            >
              <option value={4}>4 weeks</option>
              <option value={8}>8 weeks</option>
              <option value={12}>12 weeks</option>
              <option value={16}>16 weeks (full season)</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-[#2D241E] mr-2">Region:</label>
            <select 
              value={region} 
              onChange={(e) => setRegion(e.target.value)}
              className="px-4 py-2 bg-[#F0F2EB] rounded-lg"
            >
              <option value="all">All regions</option>
              <option value="New Zealand">New Zealand</option>
            </select>
          </div>
          <button
            onClick={exportCSV}
            className="ml-auto bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-[#6B9F4A]/10 to-[#B4D330]/10 rounded-2xl p-6 border border-[#6B9F4A]/20">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[#6B9F4A]" />
              <h3 className="font-semibold text-[#1A4331]">Total Growers</h3>
            </div>
            <p className="text-4xl font-bold text-[#1A4331]">{forecast?.total_growers || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Planning to plant in next {weeks} weeks</p>
          </div>
          
          <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#C9A85B]/10 rounded-2xl p-6 border border-[#D4AF37]/20">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-6 h-6 text-[#C9A85B]" />
              <h3 className="font-semibold text-[#1A4331]">Varieties Tracked</h3>
            </div>
            <p className="text-4xl font-bold text-[#1A4331]">{forecast?.total_demand_items || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Unique crop + variety combinations</p>
          </div>
          
          <div className="bg-gradient-to-br from-[#1A4331]/10 to-[#2D5F44]/10 rounded-2xl p-6 border border-[#1A4331]/20">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-6 h-6 text-[#1A4331]" />
              <h3 className="font-semibold text-[#1A4331]">Region</h3>
            </div>
            <p className="text-2xl font-bold text-[#1A4331]">{forecast?.region || 'All'}</p>
            <p className="text-sm text-[#5C665C] mt-1">Forecast window: {weeks} weeks</p>
          </div>
        </div>

        {/* Demand Table */}
        <div className="bg-white rounded-2xl border border-[#1A4331]/5 overflow-hidden">
          <div className="p-6 border-b border-[#1A4331]/5">
            <h2 className="text-2xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Top Demand by Variety
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F0F2EB]">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#2D241E]">Rank</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#2D241E]">Crop</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#2D241E]">Variety</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-[#2D241E]">Growers</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-[#2D241E]">Plants Needed</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-[#2D241E]">Peak Month</th>
                </tr>
              </thead>
              <tbody>
                {(forecast?.demand || []).slice(0, 20).map((item, idx) => (
                  <tr key={idx} className="border-b border-[#1A4331]/5 hover:bg-[#F0F2EB]/50">
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                        idx === 0 ? 'bg-[#D4AF37] text-white' :
                        idx === 1 ? 'bg-[#C9A85B] text-white' :
                        idx === 2 ? 'bg-[#B8976D] text-white' :
                        'bg-[#F0F2EB] text-[#2D241E]'
                      } font-bold`}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-[#1A4331]">{item.crop_name}</td>
                    <td className="px-6 py-4 text-[#5C665C]">{item.variety}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Users className="w-4 h-4 text-[#8EAA97]" />
                        <span className="font-bold text-[#1A4331]">{item.total_growers}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <TrendingUp className="w-4 h-4 text-[#6B9F4A]" />
                        <span className="font-bold text-[#1A4331]">{item.total_plants}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5C665C]">
                      {item.planting_dates[0]?.substring(0, 7) || 'Various'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Supplier Pitch Card */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#2D5F44] rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-3">For Seed Suppliers</h3>
          <p className="text-white/90 mb-4">
            This data represents <strong>real planting intentions</strong> from verified home gardeners 
            in New Zealand. Use this intelligence to:
          </p>
          <ul className="space-y-2 mb-6 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#B4D330]">✓</span>
              <span>Plan seed production and inventory 8-16 weeks ahead</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B4D330]">✓</span>
              <span>Target marketing to growers planning specific varieties</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B4D330]">✓</span>
              <span>Reduce waste by matching supply to actual demand</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#B4D330]">✓</span>
              <span>Identify trending varieties before they spike</span>
            </li>
          </ul>
          <p className="text-xs text-white/70">
            💡 Interested in detailed demand forecasts? Contact us about supplier partnerships.
          </p>
        </div>
      </div>
    </SupplierLayout>
  );
}

export default SupplierDashboard;
